import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useTheme } from '../context/ThemeContext';
import './Settings.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const Settings = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form states
  const [profileForm, setProfileForm] = useState({
    companyName: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // UI states
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/check-auth');
      const userData = response.data.user;
      setUser(userData);
      setProfileForm({
        companyName: userData.name || userData.first_name || '',
        email: userData.email || ''
      });
    } catch (err) {
      console.error('Error loading user:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Error loading data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleProfileChange = (e) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value
    });
    setProfileMessage('');
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    setPasswordMessage('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage('');

    try {
      await api.put('/api/user/profile', profileForm);
      setProfileMessage('✅ Profile updated successfully');
      await loadUser();
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileMessage('❌ ' + (err.response?.data?.error || 'Error updating profile'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage('');

    // Validation
    if (passwordForm.newPassword.length < 4) {
      setPasswordMessage('❌ Password must be at least 4 characters');
      setPasswordSaving(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage('❌ Passwords do not match');
      setPasswordSaving(false);
      return;
    }

    try {
      await api.put('/api/user/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setPasswordMessage('✅ Password updated successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordMessage('❌ ' + (err.response?.data?.error || 'Current password is incorrect'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);

    try {
      await api.delete('/api/user/account');

      // Logout and redirect
      localStorage.removeItem('halisoft_user');
      alert('✅ Account deleted successfully');
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('❌ Error deleting account');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-error">
        <p>{error}</p>
        <button onClick={loadUser} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1 className="page-title">⚙️ Account Settings</h1>

        {/* Profile Settings */}
        <div className="settings-section">
          <h2>👤 Profile Information</h2>

          <form onSubmit={handleProfileSubmit} className="settings-form">
            {profileMessage && (
              <div className={`form-message ${profileMessage.includes('✅') ? 'success' : 'error'}`}>
                {profileMessage}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="companyName">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={profileForm.companyName}
                onChange={handleProfileChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileForm.email}
                onChange={handleProfileChange}
                required
              />
              <small className="form-hint">
                Used for login and notifications
              </small>
            </div>

            <button
              type="submit"
              disabled={profileSaving}
              className="btn-save"
            >
              {profileSaving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </form>
        </div>

        {/* Password Settings */}
        <div className="settings-section">
          <h2>🔒 Change Password</h2>

          <form onSubmit={handlePasswordSubmit} className="settings-form">
            {passwordMessage && (
              <div className={`form-message ${passwordMessage.includes('✅') ? 'success' : 'error'}`}>
                {passwordMessage}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="new-password"
                minLength={4}
              />
              <small className="form-hint">
                At least 4 characters
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={passwordSaving}
              className="btn-save"
            >
              {passwordSaving ? (
                <>
                  <span className="spinner-small"></span>
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>

        {/* Subscription Info */}
        <div className="settings-section">
          <h2>💎 Subscription</h2>

          <div className="subscription-info">
            <div className="info-row">
              <span className="info-label">Current Plan</span>
              <span className="info-value">{user?.subscription?.plan?.name || 'FREE'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Price</span>
              <span className="info-value">
                {user?.subscription?.plan?.slug === 'enterprise'
                  ? 'Customised pricing'
                  : user?.subscription?.plan?.price === 0
                    ? 'Free'
                    : `$${user?.subscription?.plan?.price}/month`}
              </span>
            </div>

            {user?.subscription?.nextBillingDate && (
              <div className="info-row">
                <span className="info-label">Next Billing Date</span>
                <span className="info-value">
                  {new Date(user.subscription.nextBillingDate).toLocaleDateString('en-US')}
                </span>
              </div>
            )}

            <button
              onClick={() => navigate('/subscription')}
              className="btn-manage-subscription"
            >
              Manage Subscription
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="settings-section danger-zone">
          <h2>⚠️ Danger Zone</h2>

          <div className="danger-content">
            <div className="danger-info">
              <h3>Delete My Account</h3>
              <p>
                Once your account is deleted, all your data will be permanently
                erased. This action is irreversible.
              </p>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn-delete"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal danger-modal">
            <h3>⚠️ Confirm Deletion</h3>
            <p>
              This action is <strong>irreversible</strong>. All your data,
              invoices, and history will be permanently deleted.
            </p>

            <div className="form-group">
              <label htmlFor="deleteConfirm">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                id="deleteConfirm"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirm('');
                }}
                className="btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="btn-danger"
                disabled={deleting || deleteConfirm !== 'DELETE'}
              >
                {deleting ? (
                  <>
                    <span className="spinner-small"></span>
                    Deleting...
                  </>
                ) : (
                  'Delete Permanently'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
